/**
 * views/PaymentProviderLogView.js
 * ----------------------------------------------------------------------------
 * What MTN MoMo and Flutterwave said about every payment Lusuku asked them
 * for, read from the server's payment provider log
 * (controllers/paymentProviderLogController.js).
 *
 * WHY THIS PAGE EXISTS (15 Oct 2026). A cinema ticket MoMo payment failed and
 * nobody could say why: MTN's reply had been thrown away and the server log
 * keeps only its last few minutes. It then turned out MTN had not accepted a
 * single MoMo charge since 4 Aug 2026, and nothing anywhere showed it. So the
 * top of this page answers "when did each provider last accept a charge", and
 * the rest answers "what exactly did it say when it refused".
 *
 * Orders staff (orders:read), limited to their countries by the server.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import { apiListPaymentProviderLog, apiPaymentProviderLogSummary } from "../api/index.js";
import { AppButton, AppSelect } from "../components/ui";
import Loading from "../components/Loading";
import { PAGE, Pill, errorText, fmtDateTime } from "../components/fiscal/fiscalUi.js";
import ProviderEventsTable, {
  OPERATION_LABEL,
  OUTCOME,
  OutcomePill,
  PROVIDER_LABEL,
  Reason,
  fmtAgo,
  operationLabel,
  providerLabel,
} from "../components/payments/providerLogUi.js";

const PAGE_SIZE = 100;
const WINDOWS = [
  { value: "24", label: "Last 24 hours" },
  { value: "72", label: "Last 3 days" },
  { value: "168", label: "Last 7 days" },
  { value: "720", label: "Last 30 days" },
  { value: "4320", label: "Last 180 days" },
];
const EMPTY_FILTERS = Object.freeze({ provider: "", outcome: "", operation: "", hours: "72" });
// A provider with no accepted charge for this long is flagged on the page.
const QUIET_AFTER_MS = 72 * 3600 * 1000;
const ORDER_ID = /^[a-f0-9]{24}$/i;

/** Query params: the filters that are set, and an order or a provider reference. */
const toParams = (filters, lookup) => {
  const params = {};
  ["provider", "outcome", "operation"].forEach((key) => {
    if (filters[key]) params[key] = filters[key];
  });
  const term = String(lookup || "").trim();
  if (term) {
    params[ORDER_ID.test(term) ? "orderId" : "reference"] = term;
  } else {
    // A lookup searches the whole retention period; a browse uses the window.
    params.hours = filters.hours;
  }
  return params;
};

const LastAccepted = ({ provider, at, now }) => {
  const quiet = !at || now - new Date(at).getTime() > QUIET_AFTER_MS;
  return (
    <div
      style={{
        flex: "1 1 240px",
        border: `1px solid ${quiet ? "#f5d9b0" : "#e2e8f0"}`,
        background: quiet ? "#fffaf2" : "#fff",
        borderRadius: 10,
        padding: "12px 14px",
      }}
      data-testid={`last-accepted-${provider}`}
    >
      <div style={PAGE.sectionTitle}>{providerLabel(provider)}</div>
      <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>
        {at ? `Last accepted charge ${fmtAgo(at, now)}` : "No accepted charge in the log yet"}
      </div>
      <div style={{ fontSize: 12, color: quiet ? "#8a4b0a" : "#64748b", marginTop: 2 }}>
        {at ? fmtDateTime(at) : "The log began on 15 Oct 2026."}
        {at && quiet ? " Nothing accepted for over three days: check the reasons below." : null}
      </div>
    </div>
  );
};

const Totals = ({ totals }) => {
  const byProvider = useMemo(() => {
    const out = {};
    (totals || []).forEach((t) => {
      out[t.provider] = out[t.provider] || {};
      out[t.provider][t.outcome] = t.count;
    });
    return out;
  }, [totals]);
  const providers = Object.keys(byProvider);
  if (!providers.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 18px", margin: "0 0 16px", fontSize: 12, color: "#475569" }}>
      {providers.map((provider) => (
        <span key={provider} style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <strong style={{ color: "#0f172a" }}>{providerLabel(provider)}</strong>
          {Object.keys(OUTCOME).map((outcome) =>
            byProvider[provider][outcome] ? (
              <Pill key={outcome} tone={OUTCOME[outcome].tone}>
                {byProvider[provider][outcome]} {OUTCOME[outcome].label.toLowerCase()}
              </Pill>
            ) : null
          )}
        </span>
      ))}
    </div>
  );
};

const PaymentProviderLogView = () => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [lookupInput, setLookupInput] = useState("");
  const [lookup, setLookup] = useState("");
  const [rows, setRows] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);

  const params = useMemo(() => toParams(filters, lookup), [filters, lookup]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [list, sum] = await Promise.all([
        apiListPaymentProviderLog({ ...params, limit: PAGE_SIZE }),
        apiPaymentProviderLogSummary(params),
      ]);
      setRows(Array.isArray(list?.data?.data?.rows) ? list.data.data.rows : []);
      setNextBefore(list?.data?.data?.nextBefore || null);
      setSummary(sum?.data?.data || null);
      setFetchedAt(new Date());
    } catch (err) {
      if (err?.response?.status === 403 && err?.response?.data?.code !== "COUNTRY_FORBIDDEN") setDenied(true);
      else setError(errorText(err, "Could not load the payment provider log."));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    if (!denied) load();
  }, [load, denied]);

  const loadMore = async () => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    try {
      const { data } = await apiListPaymentProviderLog({ ...params, limit: PAGE_SIZE, before: nextBefore });
      setRows((current) => [...current, ...(data?.data?.rows || [])]);
      setNextBefore(data?.data?.nextBefore || null);
    } catch (err) {
      setError(errorText(err, "Could not load older calls."));
    } finally {
      setLoadingMore(false);
    }
  };

  const setFilter = (key) => (e) => {
    const value = e.target.value;
    setFilters((f) => ({ ...f, [key]: value }));
  };
  const pickOrder = (orderId) => {
    setLookupInput(orderId);
    setLookup(orderId);
  };
  const pickReason = (reason) => {
    setFilters((f) => ({ ...f, provider: reason.provider, outcome: reason.outcome, operation: reason.operation }));
  };
  const filtersActive = Boolean(lookup || filters.provider || filters.outcome || filters.operation || filters.hours !== EMPTY_FILTERS.hours);
  const clearAll = () => {
    setFilters(EMPTY_FILTERS);
    setLookupInput("");
    setLookup("");
  };

  if (denied) {
    return (
      <div style={PAGE.page}>
        <div style={PAGE.card}>
          <div style={PAGE.cardBody}>
            <div className="lk-note lk-note--warning">
              <span>
                <strong>Orders staff only.</strong> The payment provider log shows what MTN and Flutterwave said about
                customers' payments. Ask a super admin for the orders read permission if you need it.
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const now = Date.now();
  const windowLabel = lookup
    ? "the whole retention period"
    : (WINDOWS.find((w) => w.value === filters.hours)?.label || "").toLowerCase();

  return (
    <div style={PAGE.page}>
      <div style={PAGE.card}>
        <div style={PAGE.cardHeader}>
          <div>
            <h4 style={PAGE.cardTitle}>Payment provider log</h4>
            <p style={PAGE.cardSubtitle}>
              Every call Lusuku makes to MTN MoMo and Flutterwave to take a payment, with the provider's own answer.
              Phone numbers are masked and cards show only their last four digits. Keys, tokens, PINs and full card
              numbers are never recorded. Rows are kept for 180 days.
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {fetchedAt ? <span style={{ fontSize: 11, color: "#64748b" }}>Fetched {fmtDateTime(fetchedAt)}</span> : null}
            <AppButton variant="outline" icon="fa fa-sync" onClick={load} disabled={loading}>Refresh</AppButton>
          </div>
        </div>

        <div style={PAGE.cardBody}>
          {summary?.lastAccepted ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
              {Object.keys(PROVIDER_LABEL).map((provider) => (
                <LastAccepted key={provider} provider={provider} at={summary.lastAccepted[provider]} now={now} />
              ))}
            </div>
          ) : null}

          <form
            style={PAGE.toolbar}
            onSubmit={(e) => {
              e.preventDefault();
              setLookup(lookupInput.trim());
            }}
          >
            <div className="lk-field" style={{ marginBottom: 0, minWidth: 150 }}>
              <label className="lk-field__label" htmlFor="ppl-provider">Provider</label>
              <AppSelect id="ppl-provider" block value={filters.provider} onChange={setFilter("provider")}>
                <option value="">Both providers</option>
                {Object.entries(PROVIDER_LABEL).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </AppSelect>
            </div>
            <div className="lk-field" style={{ marginBottom: 0, minWidth: 160 }}>
              <label className="lk-field__label" htmlFor="ppl-outcome">Outcome</label>
              <AppSelect id="ppl-outcome" block value={filters.outcome} onChange={setFilter("outcome")}>
                <option value="">Any outcome</option>
                {Object.entries(OUTCOME).map(([key, meta]) => <option key={key} value={key}>{meta.label}</option>)}
              </AppSelect>
            </div>
            <div className="lk-field" style={{ marginBottom: 0, minWidth: 170 }}>
              <label className="lk-field__label" htmlFor="ppl-operation">Step</label>
              <AppSelect id="ppl-operation" block value={filters.operation} onChange={setFilter("operation")}>
                <option value="">Every step</option>
                {Object.entries(OPERATION_LABEL).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </AppSelect>
            </div>
            <div className="lk-field" style={{ marginBottom: 0, minWidth: 150 }}>
              <label className="lk-field__label" htmlFor="ppl-hours">Period</label>
              <AppSelect id="ppl-hours" block value={filters.hours} onChange={setFilter("hours")} disabled={Boolean(lookup)}>
                {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
              </AppSelect>
            </div>
            <div className="lk-field" style={{ marginBottom: 0, flex: "1 1 260px" }}>
              <label className="lk-field__label" htmlFor="ppl-lookup">Order ID or provider reference</label>
              <input
                id="ppl-lookup"
                className="lk-input"
                value={lookupInput}
                onChange={(e) => setLookupInput(e.target.value)}
                placeholder="24 character order ID, MTN reference or Flutterwave tx_ref"
              />
            </div>
            <AppButton type="submit" icon="fa fa-search">Find</AppButton>
            {filtersActive ? <AppButton variant="quiet" onClick={clearAll}>Clear filters</AppButton> : null}
          </form>

          {loading ? <Loading size="panel" label="Loading provider calls..." /> : null}

          {!loading && error ? (
            <div className="lk-note lk-note--danger" style={{ marginBottom: 14 }}>
              <span>{error}</span>
              <AppButton size="sm" variant="quiet" onClick={load} style={{ marginLeft: "auto" }}>Retry</AppButton>
            </div>
          ) : null}

          {!loading && !error ? (
            <>
              <Totals totals={summary?.totals} />

              <div style={{ ...PAGE.sectionTitle, marginTop: 4 }}>Why payments did not go through, {windowLabel}</div>
              <div className="lk-table-frame" style={{ marginBottom: 22 }}>
                <Table responsive hover className="lk-table" style={{ fontSize: 13, marginBottom: 0 }}>
                  <thead>
                    <tr>
                      <th>Provider and step</th>
                      <th>Outcome</th>
                      <th>What the provider said</th>
                      <th style={{ textAlign: "right" }}>Times</th>
                      <th>Last seen</th>
                      <th>Customer was told</th>
                      <th aria-label="Show calls" />
                    </tr>
                  </thead>
                  <tbody>
                    {!summary?.reasons?.length ? (
                      <tr>
                        <td colSpan={7} style={PAGE.empty}>Nothing was refused or left unanswered in this period.</td>
                      </tr>
                    ) : (
                      summary.reasons.map((reason) => (
                        <tr key={`${reason.provider}:${reason.operation}:${reason.outcome}:${reason.reason}`}>
                          <td>
                            <span style={PAGE.strong}>{providerLabel(reason.provider)}</span>
                            <span style={PAGE.meta}>{operationLabel(reason.operation)}</span>
                          </td>
                          <td>
                            <OutcomePill outcome={reason.outcome} />
                            {reason.httpStatus ? <span style={PAGE.meta}>HTTP {reason.httpStatus}</span> : null}
                          </td>
                          <td><Reason code={reason.reason} /></td>
                          <td style={{ textAlign: "right", fontWeight: 700 }}>{reason.count}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {fmtDateTime(reason.lastAt)}
                            <span style={PAGE.meta}>{fmtAgo(reason.lastAt, now)}</span>
                          </td>
                          <td style={{ maxWidth: 260, fontSize: 12, color: "#334155", overflowWrap: "anywhere" }}>
                            {reason.customerMessage || <span style={PAGE.muted}>Nothing shown</span>}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <AppButton size="sm" variant="outline" onClick={() => pickReason(reason)}>Show calls</AppButton>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              <p style={PAGE.count}>
                {rows.length === 0
                  ? "No calls"
                  : `${rows.length} call${rows.length === 1 ? "" : "s"}, newest first${nextBefore ? ", more below" : ""}`}
              </p>
              <ProviderEventsTable
                rows={rows}
                onPickOrder={pickOrder}
                emptyText={lookup ? "No provider calls were recorded for this order or reference." : "No provider calls match these filters."}
              />
              {nextBefore ? (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 14 }}>
                  <AppButton variant="outline" onClick={loadMore} loading={loadingMore}>Load older calls</AppButton>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PaymentProviderLogView;
